import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { lockBodyScroll } from '../../utils/bodyScrollLock';
import { useTranslation } from 'react-i18next';
import { Z_INDEX } from '../../utils/zIndex';
import Icon from './Icon';
import CloseButton from './CloseButton';
import Button from './Button';

// Tour Context
const TourContext = createContext();

// Tours only start when the user asks for one (2026-10-01, Kurtis). Until then
// nine tours fired by themselves on page arrival, held in check by a
// one-per-session cap and a priority queue; the phone tab bar made most of
// them redundant, so they were cut and the trip planner's became an invite
// ("Take the 1-minute tour"). With nothing auto-starting, the cap, the queue
// and the suppression hook for the /welcome flow went with them. Don't bring
// back an auto-start: a tour that dims the screen unasked is the interruption
// that refresh removed.

/**
 * First element matching `selector` that is actually on screen.
 *
 * Several surfaces render a control twice — a desktop copy hidden below `md`
 * and a phone copy (the trip planner's tab strip is one) — and the hidden one
 * usually comes first in the DOM. `querySelector` would hand back that copy,
 * whose rect is all zeros, and the spotlight would land in the top-left corner.
 */
function findVisibleTarget(selector) {
  if (!selector) return null;
  for (const el of document.querySelectorAll(selector)) {
    const rect = el.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) return el;
  }
  return null;
}

/**
 * Hook to access tour functionality
 */
export const useTour = () => {
  const context = useContext(TourContext);
  if (context === undefined) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};

/**
 * TourProvider - Manages guided tours throughout the app
 *
 * A lightweight, React 19 compatible alternative to react-joyride.
 * Supports spotlight highlighting, step-by-step navigation, and
 * automatic element targeting via data-tour attributes.
 */
export const TourProvider = ({ children }) => {
  // Tour state
  const [isActive, setIsActive] = useState(false);
  const [currentTour, setCurrentTour] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState(null);

  // Refs for positioning
  const forcedVisibleEls = useRef([]);

  // Get current step
  const currentStep = currentTour?.steps[currentStepIndex] || null;
  const totalSteps = currentTour?.steps.length || 0;
  const isLastStep = currentStepIndex === totalSteps - 1;
  const isFirstStep = currentStepIndex === 0;

  /**
   * Clear any elements we previously forced visible
   */
  const clearForcedVisible = useCallback(() => {
    forcedVisibleEls.current.forEach(el => {
      el.classList.remove('tour-force-visible');
    });
    forcedVisibleEls.current = [];
  }, []);

  /**
   * Force hover-hidden ancestors of the target element to be visible.
   * Walks up from the target looking for elements that use
   * opacity-0 + group-hover:opacity-100 (Tailwind hover pattern).
   */
  const forceHoverVisible = useCallback((element) => {
    clearForcedVisible();

    let el = element;
    while (el && el !== document.body) {
      if (el.classList.contains('opacity-0') && el.classList.contains('group-hover:opacity-100')) {
        el.classList.add('tour-force-visible');
        forcedVisibleEls.current.push(el);
      }
      el = el.parentElement;
    }
  }, [clearForcedVisible]);

  // The spotlight covers the screen, so it claims the background-scroll lock
  // for exactly as long as a tour is running.
  //
  // Keyed on `isActive` rather than taken and released by startTour/endTour,
  // even though those are the only two things that move it. An effect is
  // symmetric by construction: it releases on unmount (a leaked claim freezes
  // the page), and it survives StrictMode's setup→cleanup→setup, which a
  // claim taken in a callback does not — the cleanup would drop it, leaving
  // the spotlight up over a page that still scrolls.
  //
  // This provider sits ABOVE the Router, so a route change doesn't unmount it
  // and doesn't end a tour: the page that starts a tour ends it when it
  // unmounts (TripPlannerPage does).
  useEffect(() => {
    if (!isActive) return undefined;
    return lockBodyScroll();
  }, [isActive]);

  /**
   * Start a tour. Steps whose target isn't on screen right now are dropped
   * (a desktop-only control on a phone, an empty itinerary), so the user never
   * gets a spotlight on nothing; a step with no target at all is a centred
   * card and always kept. Nothing left → no tour.
   */
  const startTour = useCallback((tourConfig) => {
    const steps = (tourConfig?.steps || []).filter(
      step => !step.target || step.onBeforeShow || findVisibleTarget(step.target)
    );
    if (!steps.length) return;

    const firstStep = steps[0];
    if (firstStep.onBeforeShow) {
      firstStep.onBeforeShow();
    }

    setCurrentTour({ ...tourConfig, steps });
    setCurrentStepIndex(0);
    setIsActive(true);
  }, []);

  /**
   * End the current tour
   */
  const endTour = useCallback((completed = false) => {
    clearForcedVisible();
    setIsActive(false);
    setCurrentTour(null);
    setCurrentStepIndex(0);
    setTargetRect(null);

    if (completed && currentTour?.onComplete) {
      currentTour.onComplete();
    }
  }, [currentTour, clearForcedVisible]);

  /**
   * Go to next step
   */
  const nextStep = useCallback(() => {
    if (isLastStep) {
      endTour(true);
    } else {
      const nextIndex = currentStepIndex + 1;
      const nextStepConfig = currentTour?.steps[nextIndex];

      // Call onBeforeShow if defined, then advance
      if (nextStepConfig?.onBeforeShow) {
        nextStepConfig.onBeforeShow();
        // Small delay to let state update before advancing
        setTimeout(() => setCurrentStepIndex(nextIndex), 50);
      } else {
        setCurrentStepIndex(nextIndex);
      }
    }
  }, [isLastStep, endTour, currentStepIndex, currentTour]);

  /**
   * Go to previous step
   */
  const prevStep = useCallback(() => {
    if (!isFirstStep) {
      const prevIndex = currentStepIndex - 1;
      const prevStepConfig = currentTour?.steps[prevIndex];

      // Call onBeforeShow if defined, then go back
      if (prevStepConfig?.onBeforeShow) {
        prevStepConfig.onBeforeShow();
        setTimeout(() => setCurrentStepIndex(prevIndex), 50);
      } else {
        setCurrentStepIndex(prevIndex);
      }
    }
  }, [isFirstStep, currentStepIndex, currentTour]);

  /**
   * Skip the tour
   */
  const skipTour = useCallback(() => {
    if (currentTour?.onSkip) {
      currentTour.onSkip();
    }
    endTour(false);
  }, [currentTour, endTour]);

  /**
   * Update target element position
   */
  const updateTargetPosition = useCallback(() => {
    if (!currentStep?.target) {
      setTargetRect(null);
      clearForcedVisible();
      return;
    }

    // A target can go missing mid-tour (a step's onBeforeShow didn't reveal
    // it, or a resize hid it): the step falls back to the centred card.
    const element = findVisibleTarget(currentStep.target);
    if (element) {
      // Force any hover-hidden ancestors to be visible so the spotlight target is shown
      forceHoverVisible(element);

      const rect = element.getBoundingClientRect();
      setTargetRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        bottom: rect.bottom,
        right: rect.right
      });

      // Scroll element into view if needed
      if (currentStep.scrollIntoView !== false) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } else {
      setTargetRect(null);
    }
  }, [currentStep, clearForcedVisible, forceHoverVisible]);

  // Update position when step changes
  useEffect(() => {
    if (isActive && currentStep) {
      // Small delay to allow DOM to settle
      const timer = setTimeout(updateTargetPosition, 100);
      return () => clearTimeout(timer);
    }
  }, [isActive, currentStep, updateTargetPosition]);

  // Listen for resize/scroll
  useEffect(() => {
    if (!isActive) return;

    const handleUpdate = () => updateTargetPosition();

    window.addEventListener('resize', handleUpdate);
    window.addEventListener('scroll', handleUpdate, true);

    return () => {
      window.removeEventListener('resize', handleUpdate);
      window.removeEventListener('scroll', handleUpdate, true);
    };
  }, [isActive, updateTargetPosition]);

  // Handle escape key
  useEffect(() => {
    if (!isActive) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        skipTour();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isActive, skipTour]);

  const value = {
    isActive,
    currentStep,
    currentStepIndex,
    totalSteps,
    isFirstStep,
    isLastStep,
    startTour,
    endTour,
    nextStep,
    prevStep,
    skipTour
  };

  return (
    <TourContext.Provider value={value}>
      {children}
      {isActive && (
        <TourOverlay
          step={currentStep}
          stepIndex={currentStepIndex}
          totalSteps={totalSteps}
          targetRect={targetRect}
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
          onNext={nextStep}
          onPrev={prevStep}
          onSkip={skipTour}
        />
      )}
    </TourContext.Provider>
  );
};

/**
 * Tour Overlay Component - Renders the spotlight and tooltip
 */
function TourOverlay({
  step,
  stepIndex,
  totalSteps,
  targetRect,
  isFirstStep,
  isLastStep,
  onNext,
  onPrev,
  onSkip
}) {
  const { t } = useTranslation();
  const tooltipRef = useRef(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  // Calculate tooltip position with auto-flip
  useEffect(() => {
    if (!tooltipRef.current || !targetRect) return;

    const tooltip = tooltipRef.current;
    const tooltipRect = tooltip.getBoundingClientRect();
    const padding = 16;
    const arrowSize = 8;
    const gap = arrowSize + padding;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Helpers to compute position for a given placement
    const calcPosition = (p) => {
      let topPos, leftPos;
      switch (p) {
        case 'top':
          topPos = targetRect.top - tooltipRect.height - gap;
          leftPos = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2);
          break;
        case 'bottom':
          topPos = targetRect.bottom + gap;
          leftPos = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2);
          break;
        case 'left':
          topPos = targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2);
          leftPos = targetRect.left - tooltipRect.width - gap;
          break;
        case 'right':
          topPos = targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2);
          leftPos = targetRect.right + gap;
          break;
        default:
          topPos = targetRect.bottom + gap;
          leftPos = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2);
      }
      // Slide along the cross axis to stay on screen BEFORE the fit check:
      // a card centred under a tab near the edge is off-screen only because
      // of the centring, and failing it there sent the card to the side of a
      // tab strip, half over the spotlight (2026-10-01).
      if (p === 'top' || p === 'bottom') {
        leftPos = Math.min(Math.max(leftPos, padding), viewportWidth - tooltipRect.width - padding);
      } else {
        topPos = Math.min(Math.max(topPos, padding), viewportHeight - tooltipRect.height - padding);
      }
      return { top: topPos, left: leftPos };
    };

    const fitsInViewport = (pos) =>
      pos.top >= padding &&
      pos.top + tooltipRect.height <= viewportHeight - padding &&
      pos.left >= padding &&
      pos.left + tooltipRect.width <= viewportWidth - padding;

    const flipMap = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };
    const preferred = step.placement || 'bottom';

    // Try preferred placement, then flipped, then remaining two
    const candidates = [
      preferred,
      flipMap[preferred],
      ...Object.keys(flipMap).filter(p => p !== preferred && p !== flipMap[preferred])
    ];

    let top, left;
    let placed = false;
    for (const p of candidates) {
      const pos = calcPosition(p);
      if (fitsInViewport(pos)) {
        top = pos.top;
        left = pos.left;
        placed = true;
        break;
      }
    }

    // Fallback: use preferred placement and clamp to viewport
    if (!placed) {
      const pos = calcPosition(preferred);
      top = pos.top;
      left = pos.left;
    }

    // Clamp horizontal/vertical to stay in viewport
    if (left < padding) left = padding;
    if (left + tooltipRect.width > viewportWidth - padding) {
      left = viewportWidth - tooltipRect.width - padding;
    }
    if (top < padding) top = padding;
    if (top + tooltipRect.height > viewportHeight - padding) {
      top = viewportHeight - tooltipRect.height - padding;
    }

    setTooltipPosition({ top, left });
  }, [targetRect, step]);

  // If no target, show centered modal
  const isCentered = !targetRect;

  const overlay = (
    <div
      className="fixed inset-0"
      style={{ zIndex: Z_INDEX.TOUR_BACKDROP }}
    >
      {/* Backdrop with spotlight cutout */}
      {targetRect ? (
        <svg className="absolute inset-0 w-full h-full">
          <defs>
            <mask id="spotlight-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              <rect
                x={targetRect.left - 8}
                y={targetRect.top - 8}
                width={targetRect.width + 16}
                height={targetRect.height + 16}
                rx="8"
                fill="black"
              />
            </mask>
          </defs>
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill="rgba(0, 0, 0, 0.55)"
            mask="url(#spotlight-mask)"
          />
        </svg>
      ) : (
        <div className="absolute inset-0 bg-black/55" />
      )}

      {/* Spotlight ring animation */}
      {targetRect && (
        <div
          className="absolute rounded-lg animate-spotlight pointer-events-none"
          style={{
            top: targetRect.top - 8,
            left: targetRect.left - 8,
            width: targetRect.width + 16,
            height: targetRect.height + 16,
            zIndex: Z_INDEX.TOUR_SPOTLIGHT
          }}
        />
      )}

      {/* Tooltip. Its width is set, never shrink-to-fit: an absolutely
          positioned box with left:50% sizes itself into the half of the screen
          to its right, which on a phone squeezed the card to ~195px. The
          centred card sits in a flex container for the same reason. */}
      <div
        className={isCentered ? 'absolute inset-0 flex items-center justify-center p-4 pointer-events-none' : ''}
        style={isCentered ? { zIndex: Z_INDEX.TOUR_TOOLTIP } : undefined}
      >
        <div
          ref={tooltipRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={step.title ? 'tour-step-title' : undefined}
          className={`bg-surface rounded-modal shadow-elevation-4 w-[calc(100vw-2rem)] max-w-sm animate-tooltip-in pointer-events-auto ${
            isCentered ? 'relative' : 'absolute'
          }`}
          style={isCentered ? undefined : {
            top: tooltipPosition.top,
            left: tooltipPosition.left,
            zIndex: Z_INDEX.TOUR_TOOLTIP
          }}
        >
          {/* Close button */}
          <CloseButton
            variant="plain"
            onClick={onSkip}
            ariaLabel={t('tour.closeAriaLabel')}
            className="absolute top-0.5 right-0.5"
          />

          {/* Content */}
          <div className="p-5 pr-12">
            {step.icon && (
              <div className="mb-3">
                <Icon name={step.icon} className="w-8 h-8 text-primary" />
              </div>
            )}

            {step.title && (
              <h3 id="tour-step-title" className="font-display font-medium text-xl text-content-primary mb-1.5">
                {step.title}
              </h3>
            )}

            <p className="text-content-secondary text-sm leading-relaxed">
              {step.content}
            </p>
          </div>

          {/* Footer */}
          <div className="px-5 py-3 bg-surface-secondary rounded-b-modal flex items-center justify-between gap-3">
            {/* Progress indicator */}
            <div className="flex items-center gap-1 min-w-0">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full flex-shrink-0 transition-colors duration-fast ${
                    i === stepIndex ? 'bg-primary' : 'bg-border'
                  }`}
                />
              ))}
              <span className="ml-2 text-xs text-content-muted tabular-nums whitespace-nowrap">
                {t('tour.progressLabel', { current: stepIndex + 1, total: totalSteps })}
              </span>
            </div>

            {/* Navigation buttons */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {!isFirstStep && (
                <Button variant="ghost" size="sm" onClick={onPrev}>
                  {t('tour.backButton')}
                </Button>
              )}
              <Button size="sm" onClick={onNext}>
                {isLastStep ? t('tour.doneButton') : t('tour.nextButton')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}

export default TourProvider;
